import { useModals } from 'almanac-ui/Modal'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ArticleService } from 'api/services/ArticleService'
import { ConfigureMoveFileAutomationModal } from 'components/ConfigureMoveFileAutomationModal/ConfigureMoveFileAutomationModal'
import { CopyFileActionConfigModal } from 'components/CopyFileActionConfigModal/CopyFileActionConfigModal'
import { CreateAccessesActionConfigModal } from 'components/CreateAccessesActionConfigModal/CreateAccessesActionConfigModal'
import { ExportModal } from 'components/ExportModal/ExportModal'
import { useCallback } from 'react'
import { AssigneeType, SelectPropertyValue } from '../api/clients/PropertyClient'
import { CloseReviewActionModal } from '../components/CloseReviewsActionModal/CloseReviewActionModal'
import { CreateReviewsActionConfigModal } from '../components/CreateReviewActionConfigModal/CreateReviewActionConfigModal'
import { ReviewType, ReviewerRole } from '../components/ReviewsWizard/types'
import { Action } from '../stores/Workflows/Action'
import { State } from '../stores/Workflows/State'
import {
  CloseReviewActionConfig,
  CopyFileActionConfig,
  CreateAccessesActionConfig,
  CreateReviewActionConfig,
  MoveFileActionConfig,
  PersistedPropertyValue,
} from '../stores/Workflows/Types'
import { useActionModalPrompt } from './useActionModalPrompt'

export interface Assignee {
  type: AssigneeType
  id?: string
  title?: string
}

interface ExecutionContext {
  currentState: State
  nextState: State
  assignee: Assignee
}

const reviewerRolesForActionType = {
  'reviews.create': [ReviewerRole.REVIEWER],
  'approvals.create': [ReviewerRole.APPROVER],
  'read_receipts.create': [ReviewerRole.VIEWER],
}

const reviewTypeForActionType = {
  'reviews.create': ReviewType.FEEDBACK,
  'approvals.create': ReviewType.APPROVAL,
  'read_receipts.create': ReviewType.READ_RECEIPTS,
}

export const useExecuteWorkflowActions = () => {
  const prompt = useActionModalPrompt()
  const { openModal } = useModals()

  return useCallback(
    async (workflowActions: Action[], context: ExecutionContext) => {
      return Promise.all(
        workflowActions.map((workflowAction) => {
          switch (workflowAction.type) {
            case 'reviews.close':
              return prompt<Partial<CloseReviewActionConfig>, { toState: State; fromState: State }>(
                CloseReviewActionModal,
                {
                  toState: context.nextState,
                  fromState: context.currentState,
                  message: '',
                },
              ).then((result) => ({ 'reviews.close': result }))
            case 'reviews.create':
            case 'approvals.create':
            case 'read_receipts.create':
              const reviewerRoles = reviewerRolesForActionType[workflowAction.type]
              const type = reviewTypeForActionType[workflowAction.type]

              return prompt<
                Partial<CreateReviewActionConfig>,
                {
                  articleId?: string
                  to: SelectPropertyValue
                  connectedTo?: SelectPropertyValue
                  reviewType: ReviewType
                }
              >(CreateReviewsActionConfigModal, {
                ...workflowAction.config,
                reviewer_roles: reviewerRoles,
                reviewType: type,
                articleId: context.assignee.id,
                to: context.nextState.propertyValue as PersistedPropertyValue,
                connectedTo: (context.nextState.childStates[0] as State | undefined)
                  ?.propertyValue as PersistedPropertyValue,
                title: context.assignee.title,
              }).then((result) => ({ [workflowAction.type]: result }))

            case 'files.export':
              if (!context.assignee.id) {
                // In the event of creating a new file, the assignee id will not be available, in which case, skip.
                return { [workflowAction.type]: null }
              }
              ArticleService.getArticle(context.assignee.id).then((res) => {
                if (res.type === SUCCESS_RESPONSE_TYPE) {
                  openModal(
                    ExportModal,
                    {
                      articleId: res.data.article.id,
                    },
                    { newStack: true },
                  )
                }
              })
              return { [workflowAction.type]: {} }

            case 'files.move':
              return prompt<
                Partial<MoveFileActionConfig>,
                {
                  workflowFolderKey: string
                }
              >(ConfigureMoveFileAutomationModal, {
                ...workflowAction.config,
                workflowFolderKey: workflowAction.state.workflow.folder.key,
              }).then((result) => ({ [workflowAction.type]: result }))

            case 'accesses.create':
              return prompt<Partial<CreateAccessesActionConfig>, { assignee: Assignee }>(
                CreateAccessesActionConfigModal,
                {
                  ...workflowAction.config,
                  assignee: context.assignee,
                },
              ).then((result) => ({ [workflowAction.type]: result }))

            case 'files.copy':
              return prompt<
                Partial<CopyFileActionConfig>,
                {
                  workflowFolderKey: string
                  assignee: Assignee
                }
              >(CopyFileActionConfigModal, {
                ...workflowAction.config,
                assignee: context.assignee,
                workflowFolderKey: workflowAction.state.workflow.folder.key,
              }).then((result) => ({ [workflowAction.type]: result }))

            default:
              return Promise.resolve({ [workflowAction.type]: null })
          }
        }),
      )
    },
    [prompt, openModal],
  )
}
